/**
 * A repair service's customer-facing blurb — the text under the service on
 * the quote, and (since 2026-10-16) appended to the product's catalog
 * description on its invoice line (`withCatalogDescription`).
 *
 * The quote preview and the Generate invoice / ticket dialogs both read it
 * from here, and sm4rt-api's `_repair_service_blurb` (quote_service.py) builds
 * the same string for the line it actually sends to RepairShopr. **Change the
 * two together**, or the invoice stops saying what the customer was quoted.
 */

import { DEFAULT_INCLUDES_CLOSING_LINE } from '@techgitter/contracts';
import type { QuoteDetail } from '@techgitter/contracts';

import { parseLeadTimeUpperDays } from './leadTime';

type RepairServiceEntry = QuoteDetail['repairServices'][number];

const round2 = (n: number): number => Math.round(n * 100) / 100;

export const INCLUDES_ALL_PARTS_LABOUR = 'Includes all parts and labour';

const PART_BULLET = '•';

/**
 * Lead-time text for a repair service — sourced from the accepted
 * third-party component on the service (if any). Empty string when
 * there's no accepted third-party line or it has no lead time set.
 */
export const acceptedThirdPartyLeadTime = (svc: RepairServiceEntry): string => {
  const accepted = (svc.components ?? []).find(
    (c) => c.kind === 'third_party' && c.vendorQuoteStatus === 'accepted',
  );
  return (accepted?.leadTime ?? '').trim();
};

/**
 * Longest lead-time label across a repair service's ORDERABLE parts
 * (`kind === 'part'`, non-stock — the ones we actually order in). "Longest"
 * is by upper-bound calendar days, and we return that part's original label
 * (e.g. two parts "1 week" + "2-3 weeks" → "2-3 weeks"). Empty string when no
 * orderable part carries a lead time. Independent of the third-party lead-time
 * line — parts arriving vs sub-contracted work returning are distinct.
 */
export const orderablePartsLongestLeadTime = (
  svc: RepairServiceEntry,
): string => {
  let bestLabel = '';
  let bestDays = 0;
  for (const c of svc.components ?? []) {
    if (c.kind !== 'part' || c.fulfillmentSource === 'stock') continue;
    const label = (c.leadTime ?? '').trim();
    if (!label) continue;
    const days = parseLeadTimeUpperDays(label);
    if (days != null && days > bestDays) {
      bestDays = days;
      bestLabel = label;
    }
  }
  return bestLabel;
};

/**
 * "Includes:" block for a repair service whose `includePartDetails` toggle is
 * on. Renders a header line followed by one bulleted line per part component
 * (in component order, prefixed with its whole-unit quantity; parts with no
 * typed description are skipped), then closes with the editable closing
 * clause. Newline-delimited so each part reads as its own line in both the
 * HTML preview (`\n` → `<br />`) and the plain-text copy — large
 * network-install kits were unreadable as one comma-run.
 */
export const buildPartDetailsText = (svc: RepairServiceEntry): string => {
  const items: string[] = [];
  for (const c of svc.components ?? []) {
    if (c.kind !== 'part') continue;
    const desc = (c.description || '').trim();
    if (!desc) continue;
    const qty =
      Number.isFinite(c.units as number) && (c.units as number) > 0
        ? (c.units as number)
        : 1;
    const qtyLabel = Number.isInteger(qty) ? String(qty) : String(round2(qty));
    items.push(`${qtyLabel}x ${desc}`);
  }
  // `undefined` (never edited) falls back to the default; an
  // explicitly-cleared empty string omits the closing bullet entirely.
  const closing =
    svc.includesClosingLine === undefined || svc.includesClosingLine === null
      ? DEFAULT_INCLUDES_CLOSING_LINE
      : svc.includesClosingLine.trim();
  if (closing) items.push(closing);
  const bulleted = items.map((line) => `${PART_BULLET} ${line}`).join('\n');
  return `Includes:\n${bulleted}`;
};

/**
 * `text` followed by the service's blurb lines: the third-party lead time,
 * the Quote and Invoice Preview Options choice (itemised parts, or the
 * blanket "Includes all parts and labour" — the default when neither flag is
 * set), then the ordered-parts notice. Standalone parts are plain product
 * lines and get `text` alone.
 */
export const repairServiceBlurb = (
  svc: RepairServiceEntry,
  text: string,
): string => {
  if (svc.isStandalonePart) return text;
  const leadTime = acceptedThirdPartyLeadTime(svc);
  let block = leadTime ? `${text}\nLead time: ${leadTime}` : text;
  // Mutually exclusive (enforced by the editor's radio group).
  if (svc.includePartDetails) {
    block += `\n${buildPartDetailsText(svc)}`;
  } else {
    block += `\n${INCLUDES_ALL_PARTS_LABOUR}`;
  }
  // When the service has orderable parts with a lead time, set the
  // customer's expectation and the payment-before-order policy.
  const partsLeadTime = orderablePartsLongestLeadTime(svc);
  if (partsLeadTime) {
    block +=
      `\nPlease allow ${partsLeadTime} for ordered parts to arrive. ` +
      `TechSolvers does not order parts until payment in full is received.`;
  }
  return block;
};

/**
 * An invoice line's description: the RepairShopr product's own catalog
 * description, then the blurb on the next line. The catalog text is dropped
 * when empty or already the blurb's first line (a fixed-price service seeds
 * its description from the product's), so it never reads twice. Mirrors
 * `_with_catalog_description` in sm4rt-api.
 */
export const withCatalogDescription = (
  catalogDescription: string | undefined,
  blurb: string,
): string => {
  const catalog = (catalogDescription ?? '').trim();
  if (!catalog || blurb.split('\n', 1)[0].trim() === catalog) return blurb;
  return `${catalog}\n${blurb}`;
};
